import { faFileCsv, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from '@mui/icons-material/Clear';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import InfoIcon from '@mui/icons-material/Info';
import PaidIcon from '@mui/icons-material/Paid';
import SearchIcon from '@mui/icons-material/Search';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import WarningIcon from '@mui/icons-material/Warning';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    ButtonGroup,
    DialogContent,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Theme } from '@mui/system';
import {
    DataGridPro,
    GridCellParams,
    GridColDef,
    GridColumnHeaderParams,
    GridFilterModel,
    GridLogicOperator,
    GridRenderCellParams,
    GridSortModel,
    GridValueGetterParams,
} from '@mui/x-data-grid-pro';
import { LocalizationProvider, StaticDateRangePicker } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import dinero from 'dinero.js';
import { parse as parseCsv } from 'json2csv';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { OnlyFansMetricType } from '../../../../hooks/useSextforceMetricsCampaignsOverview';
import {
    OnlyFansMetricUserSalesDetails,
    OnlyFansMetricUserSalesDetailsRiskLevel,
} from '../../../../hooks/useSextforceMetricsUsersSalesDetails';
import { d2f, getPrettyDateDiff, metricTypeName } from '../../../../utils/common';
import DialogAdaptive from '../../../common/DialogAdaptive';
import RangeProgressBar, { normalizeDateToPercentage } from '../../../common/RangeProgress';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';
import SextforceMetricsCountChip from './SextforceMetricsCountChip';
import SextforceMetricsUsersWithRiskLevelCards, { UserRowWithTimeline } from './SextforceMetricsUsersWithRiskLevelCards';
import {
    EMPTY_SUBSCRIBED_DATE_RANGE,
    filterUsersBySubscribedAtRange,
    formatSubscribedDateRange,
    isSubscribedDateRangeActive,
    SubscribedDateRange,
} from './subscribedDateRangeFilter';
import {
    getRiskLevelColor,
    getRiskLevelLabel,
    searchUsers,
    sortUsers,
    subscribedAtString,
    USER_SORT_OPTIONS,
    userTotalChargebacks,
    userTotalSpent,
} from './usersListHelpers';

const PAGE_SIZE_OPTIONS = [10, 20, 40, 80, 100];

type UsersFilter = 'all' | 'spenders' | 'freeloaders' | 'previouslySubscribed' | 'chargebacks' | 'suspicious';

/**
 * Transforms the transactions data rows from the server to a format ready for export to CSV or Excel
 */
const transformTransactionsForExport = (rows: OnlyFansMetricUserSalesDetails[], showEarningsAsGross: boolean, timezone: string) => {
    const headers = [
        {
            label: `Subscribed At ${timezone}`,
            value: 'subscribedAt',
        },
        {
            label: 'Fan ID',
            value: 'userId',
        },
        {
            label: 'Fan Name',
            value: 'userName',
        },
        {
            label: 'Risk Level',
            value: 'riskLevel',
        },
        {
            label: 'Total Chargebacks ($USD)',
            value: 'totalChargebacks',
        },
        {
            label: 'Total Spent ($USD)',
            value: 'total',
        },
    ];

    const data = rows.map(row => {
        const totalChargebacks = showEarningsAsGross ? d2f(row.totalChargebacks) : d2f(row.totalChargebacks) * 0.8;
        const total = showEarningsAsGross ? d2f(row.total) : d2f(row.total) * 0.8;

        return {
            subscribedAt: row.subscribedAt ? moment(row.subscribedAt).tz(timezone).format('L hh:mm a') : 'Not Found',
            userId: row.userId,
            userName: row.userName,
            riskLevel: getRiskLevelLabel(row.riskLevel),
            totalChargebacks: totalChargebacks.toFixed(2),
            total: total.toFixed(2),
        };
    });

    return { headers, rows: data };
};

const formatMoney = (amount: number) => dinero({ amount: Math.round(amount * 100), currency: 'USD' }).toFormat();

const moneyCell = (params: any, showEarningsAsGross: boolean, showEarningsWithSubscriptions: boolean) => (
    <div style={{ fontFamily: 'monospace' }}>
        {formatMoney(userTotalSpent(params.row, showEarningsAsGross, showEarningsWithSubscriptions))}
    </div>
);

const moneyCellChargebacks = (params: any, showEarningsAsGross: boolean, showEarningsWithSubscriptions: boolean) => (
    <div style={{ fontFamily: 'monospace' }}>
        {formatMoney(userTotalChargebacks(params.row, showEarningsAsGross, showEarningsWithSubscriptions))}
    </div>
);

const countUsersSubscribedBeforeStartDate = (
    promoCampaignTotalSales: OnlyFansMetricUserSalesDetails[] | undefined,
    metricStartDate: Date | undefined,
) => {
    if (metricStartDate === undefined || !promoCampaignTotalSales) {
        return 0;
    }

    return promoCampaignTotalSales.filter(user => moment(user.subscribedAt).isBefore(metricStartDate)).length;
};

const filterUsersList = (
    data: OnlyFansMetricUserSalesDetails[] | undefined,
    filterUsers: UsersFilter,
    metricStartDate: Date | undefined,
) => {
    if (!data) {
        return [];
    }

    if (filterUsers === 'all') {
        return data;
    }

    if (filterUsers === 'spenders') {
        return data.filter((user: any) => d2f(user.total) > 0);
    }

    if (filterUsers === 'freeloaders') {
        return data.filter((user: any) => d2f(user.total) === 0);
    }

    if (filterUsers === 'previouslySubscribed') {
        if (!metricStartDate) {
            return [];
        }

        return data.filter((user: any) => moment(user.subscribedAt).isBefore(moment(metricStartDate)));
    }

    if (filterUsers === 'chargebacks') {
        return data.filter((user: any) => d2f(user.totalChargebacks) > 0);
    }

    if (filterUsers === 'suspicious') {
        return data.filter(user => user.riskLevel > OnlyFansMetricUserSalesDetailsRiskLevel.NONE);
    }

    return data;
};

const laterDate = (date1: Date, date2: Date) => {
    return date1 > date2 ? date1 : date2;
};

type Props = {
    metricId: string | undefined;
    promoCampaignTotalSales: OnlyFansMetricUserSalesDetails[] | undefined;
    promoCampaignTotalSalesLoading: boolean;
    showEarningsWithSubscriptions: boolean;
    showEarningsAsGross: boolean;
    metricStartDate: Date | undefined;
    metricType: OnlyFansMetricType | undefined;
    timezone: string;
    theme: Theme;
};

const SextforceMetricsUsersWithRiskLevelList = (props: Props) => {
    const {
        metricId,
        promoCampaignTotalSales,
        promoCampaignTotalSalesLoading,
        showEarningsAsGross,
        showEarningsWithSubscriptions,
        metricStartDate,
        metricType,
        timezone,
        theme,
    } = props;
    // Cards below the md breakpoint, table above it. The picker switches to its single-calendar layout on phones.
    const isCompact = useMediaQuery(theme.breakpoints.down('md'));
    const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
    const [limit, setLimit] = useState<number>(isCompact ? 10 : 20);
    const [offset, setOffset] = useState<number>(0);
    const [reportSort, setReportSort] = useState<GridSortModel>([{ field: 'total', sort: 'desc' }]);
    const [search, setSearch] = useState<string>('');
    const [filter, setFilter] = useState<GridFilterModel>({ items: [] });
    const [freeloadersCount, setFreeloadersCount] = useState<number>(0);
    const [spendersCount, setSpendersCount] = useState<number>(0);
    const [chargebacksCount, setChargebacksCount] = useState<number>(0);
    const [suspiciousCount, setSuspiciousCount] = useState<number>(0);
    const [filterUsers, setFilterUsers] = useState<UsersFilter>('all');
    // Optional subscription date filter. Off by default ([null, null]).
    const [subscribedDateRange, setSubscribedDateRange] = useState<SubscribedDateRange>(EMPTY_SUBSCRIBED_DATE_RANGE);
    const [draftSubscribedDateRange, setDraftSubscribedDateRange] = useState<SubscribedDateRange>(EMPTY_SUBSCRIBED_DATE_RANGE);
    const [subscribedDateDialogOpen, setSubscribedDateDialogOpen] = useState<boolean>(false);
    const isSubscribedDateFilterActive = isSubscribedDateRangeActive(subscribedDateRange);

    // Apply the subscription date range first so the chips, their counts and the exports all respect it
    const dateFilteredUsers = useMemo(
        () => filterUsersBySubscribedAtRange(promoCampaignTotalSales, subscribedDateRange, timezone),
        [promoCampaignTotalSales, subscribedDateRange, timezone],
    );

    const filteredUsersList = useMemo(
        () => filterUsersList(dateFilteredUsers, filterUsers, metricStartDate),
        [dateFilteredUsers, filterUsers, metricStartDate],
    );

    useEffect(() => {
        if (promoCampaignTotalSales && !promoCampaignTotalSalesLoading) {
            let freeloaders = 0;
            let spenders = 0;
            let chargebacks = 0;
            let suspicious = 0;

            for (const user of dateFilteredUsers) {
                const amount = d2f(user.total);

                if (amount === 0) {
                    freeloaders++;
                } else {
                    spenders++;
                }

                const chargebackAmount = d2f(user.totalChargebacks);

                if (chargebackAmount > 0) {
                    chargebacks++;
                }

                if (user.riskLevel > OnlyFansMetricUserSalesDetailsRiskLevel.NONE) {
                    suspicious++;
                }
            }

            setFreeloadersCount(freeloaders);
            setSpendersCount(spenders);
            setChargebacksCount(chargebacks);
            setSuspiciousCount(suspicious);
        } else {
            setFreeloadersCount(0);
            setSpendersCount(0);
            setChargebacksCount(0);
            setSuspiciousCount(0);
        }
    }, [promoCampaignTotalSales, promoCampaignTotalSalesLoading, dateFilteredUsers]);

    // Back to the first page whenever the visible set changes
    useEffect(() => {
        setOffset(0);
    }, [promoCampaignTotalSales, search, filterUsers, subscribedDateRange, reportSort]);

    const openSubscribedDateDialog = () => {
        setDraftSubscribedDateRange(subscribedDateRange);
        setSubscribedDateDialogOpen(true);
    };

    const applySubscribedDateRange = () => {
        setSubscribedDateRange(draftSubscribedDateRange);
        setSubscribedDateDialogOpen(false);
    };

    const clearSubscribedDateRange = () => {
        setSubscribedDateRange(EMPTY_SUBSCRIBED_DATE_RANGE);
        setDraftSubscribedDateRange(EMPTY_SUBSCRIBED_DATE_RANGE);
        setSubscribedDateDialogOpen(false);
    };

    // Memoize processed rows for timeline calculations
    const processedRows = useMemo<UserRowWithTimeline[]>(() => {
        return filteredUsersList.map(row => {
            if (!metricStartDate || !row.subscribedAt || !row.firstSaleDate || !row.lastSaleDate) {
                return { ...row, timelineValue: 0 };
            }

            const startDate = laterDate(new Date(row.subscribedAt), new Date(metricStartDate));
            const endDate = new Date();
            const firstSale = new Date(row.firstSaleDate);
            const lastSale = new Date(row.lastSaleDate);

            const timelineValue =
                normalizeDateToPercentage(startDate, endDate, lastSale) - normalizeDateToPercentage(startDate, endDate, firstSale);

            return { ...row, timelineValue };
        });
    }, [filteredUsersList, metricStartDate]);

    // The table applies search and sort itself; the card list needs them done here. The count is shared.
    const searchedRows = useMemo(() => searchUsers(processedRows, search), [processedRows, search]);
    const cardRows = useMemo(() => (isCompact ? sortUsers(searchedRows, reportSort) : searchedRows), [isCompact, searchedRows, reportSort]);

    // Define DataGrid columns
    const columns = useMemo<GridColDef<OnlyFansMetricUserSalesDetails>[]>(
        () => [
            {
                field: 'icon',
                headerName: '',
                width: 24,
                align: 'center',
                renderHeader: params => <div style={{ fontWeight: 'bold' }}>{params.colDef.headerName}</div>,
                renderCell: params =>
                    d2f(params.row.total) > 0 ? (
                        <PaidIcon
                            color={
                                typeof params.row.totalChargebacks === 'object' && d2f(params.row.totalChargebacks) !== 0
                                    ? 'warning'
                                    : 'success'
                            }
                        />
                    ) : (
                        <ThumbDownIcon color="error" />
                    ),
            },
            {
                field: 'riskLevel',
                headerName: 'Risk',
                width: 60,
                align: 'center',
                renderHeader: params => <div style={{ fontWeight: 'bold' }}>{params.colDef.headerName}</div>,
                renderCell: params => {
                    if (params.row.riskLevel === OnlyFansMetricUserSalesDetailsRiskLevel.NONE) {
                        return null;
                    }
                    return (
                        <Tooltip title={getRiskLevelLabel(params.row.riskLevel)}>
                            <WarningIcon sx={{ color: getRiskLevelColor(params.row.riskLevel, theme), fontSize: 20 }} />
                        </Tooltip>
                    );
                },
                sortComparator: (v1: any, v2: any) => v1 - v2,
            },
            {
                field: 'subscribedAt',
                headerName: 'Subscribed',
                width: 160,
                renderHeader: params => <div style={{ fontWeight: 'bold' }}>{params.colDef.headerName}</div>,
                valueGetter: (params: GridValueGetterParams) =>
                    params.row.subscribedAt && moment(params.row.subscribedAt).isValid() ? new Date(params.row.subscribedAt) : null,
                sortable: true,
                sortComparator: (v1: any, v2: any) => {
                    const date1 = v1 ? new Date(v1) : null;
                    const date2 = v2 ? new Date(v2) : null;
                    if (!date1 && !date2) return 0;
                    if (!date1) return 1;
                    if (!date2) return -1;
                    return date1 && date2 ? date1.getTime() - date2.getTime() : 0;
                },
                renderCell: params => (
                    <Typography
                        variant="inherit"
                        color={
                            params.row.riskLevel > OnlyFansMetricUserSalesDetailsRiskLevel.NONE
                                ? getRiskLevelColor(params.row.riskLevel, theme)
                                : 'inherit'
                        }
                    >
                        {subscribedAtString(params.value, timezone)}
                    </Typography>
                ),
            },
            {
                field: 'userId',
                headerName: 'ID',
                width: 100,
                renderHeader: (params: GridColumnHeaderParams<any, OnlyFansMetricUserSalesDetails, any>) => (
                    <div style={{ fontWeight: 'bold' }}>{params.colDef.headerName}</div>
                ),
                valueGetter: (params: GridValueGetterParams) => params.row.userId && params.row.userId,
                renderCell: (params: GridCellParams) => (
                    <Link href={`https://onlyfans.com/${params.row.userName}`} target="_blank" rel="noopener noreferrer">
                        <Typography
                            variant="inherit"
                            color={
                                params.row.riskLevel > OnlyFansMetricUserSalesDetailsRiskLevel.NONE
                                    ? getRiskLevelColor(params.row.riskLevel, theme)
                                    : 'inherit'
                            }
                        >
                            {params.row.userId}
                        </Typography>
                    </Link>
                ),
            },
            {
                field: 'userName',
                headerName: 'Username',
                flex: 2,
                minWidth: 150,
                renderHeader: params => <div style={{ fontWeight: 'bold' }}>{params.colDef.headerName}</div>,
                valueGetter: (params: GridValueGetterParams) => params.row.userName && params.row.userName,
                renderCell: (params: GridCellParams) => (
                    <Link href={`https://onlyfans.com/${params.row.userName}`} target="_blank" rel="noopener noreferrer">
                        <Typography
                            variant="inherit"
                            color={
                                params.row.riskLevel > OnlyFansMetricUserSalesDetailsRiskLevel.NONE
                                    ? getRiskLevelColor(params.row.riskLevel, theme)
                                    : 'inherit'
                            }
                        >
                            {params.row.userName}
                        </Typography>
                    </Link>
                ),
            },
            {
                field: 'timeline',
                headerName: 'Spending Timeline',
                width: 170,
                renderHeader: params => <div style={{ fontWeight: 'bold' }}>{params.colDef.headerName}</div>,
                valueGetter(params) {
                    return metricStartDate && params.row.firstSaleDate && params.row.lastSaleDate
                        ? normalizeDateToPercentage(
                              laterDate(new Date(params.row.subscribedAt), new Date(metricStartDate)),
                              new Date(),
                              new Date(params.row.lastSaleDate),
                          ) -
                              normalizeDateToPercentage(
                                  laterDate(new Date(params.row.subscribedAt), new Date(metricStartDate)),
                                  new Date(),
                                  new Date(params.row.firstSaleDate),
                              )
                        : 0;
                },
                renderCell: (params: GridRenderCellParams<OnlyFansMetricUserSalesDetails>) => (
                    <div style={{ width: '100%' }}>
                        {metricStartDate && params.row.subscribedAt && params.row.firstSaleDate && params.row.lastSaleDate && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Tooltip
                                    placement="top"
                                    title={
                                        <Stack direction="column" spacing={1}>
                                            <div style={{ fontWeight: 'bold' }}>
                                                Spending{' '}
                                                {params.value.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                    minimumIntegerDigits: 2,
                                                })}
                                                % of total time
                                            </div>
                                            <div>
                                                <strong>First Sale</strong>
                                                <br />
                                                After {getPrettyDateDiff(new Date(metricStartDate), new Date(params.row.firstSaleDate))}
                                                <br />
                                                {moment(params.row.firstSaleDate).format('L hh:mm A')}
                                            </div>
                                            <div>
                                                <strong>Last Sale</strong>
                                                <br />
                                                After {getPrettyDateDiff(new Date(metricStartDate), new Date(params.row.lastSaleDate))}
                                                <br />
                                                {moment(params.row.lastSaleDate).format('L hh:mm A')}
                                            </div>
                                        </Stack>
                                    }
                                >
                                    <div style={{ width: '100%' }}>
                                        <RangeProgressBar
                                            minValue={normalizeDateToPercentage(
                                                laterDate(new Date(params.row.subscribedAt), new Date(metricStartDate)),
                                                new Date(),
                                                new Date(params.row.firstSaleDate),
                                            )}
                                            maxValue={normalizeDateToPercentage(
                                                laterDate(new Date(params.row.subscribedAt), new Date(metricStartDate)),
                                                new Date(),
                                                new Date(params.row.lastSaleDate),
                                            )}
                                            sx={{ cursor: 'help' }}
                                        />
                                    </div>
                                </Tooltip>
                                <div style={{ fontFamily: 'monospace' }}>
                                    {params.value.toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                        minimumIntegerDigits: 2,
                                    })}
                                    %
                                </div>
                            </Stack>
                        )}
                    </div>
                ),
                sortable: true,
                sortComparator: (v1: any, v2: any) => v1 - v2,
            },
            {
                field: 'totalChargebacks',
                headerName: `Chargebacks (${showEarningsAsGross ? 'gross' : 'net'})`,
                flex: 1,
                minWidth: 100,
                align: 'right',
                headerAlign: 'right',
                renderHeader: params => <div style={{ fontWeight: 'bold' }}>{params.colDef.headerName}</div>,
                sortComparator: (v1: any, v2: any) => d2f(v1) - d2f(v2),
                renderCell: params => moneyCellChargebacks(params, showEarningsAsGross, showEarningsWithSubscriptions),
            },
            {
                field: 'total',
                headerName: `Total (${showEarningsAsGross ? 'gross' : 'net'})`,
                flex: 1,
                minWidth: 100,
                align: 'right',
                headerAlign: 'right',
                renderHeader: params => <div style={{ fontWeight: 'bold' }}>{params.colDef.headerName}</div>,
                sortComparator: (v1: any, v2: any) => d2f(v1) - d2f(v2),
                renderCell: params => moneyCell(params, showEarningsAsGross, showEarningsWithSubscriptions),
            },
        ],
        [showEarningsAsGross, timezone, theme, showEarningsWithSubscriptions, metricStartDate],
    );

    useEffect(() => {
        if (search.trim().length === 0) {
            setFilter({ items: [] });
        } else {
            setFilter({
                items: [
                    {
                        id: 1,
                        field: 'userName',
                        operator: 'contains',
                        value: search,
                    },
                    {
                        id: 2,
                        field: 'userId',
                        operator: 'contains',
                        value: search,
                    },
                ],
                logicOperator: GridLogicOperator.Or,
            });
        }
    }, [search]);

    // Convert report to CSV format and start file download
    const handleDownloadReportCsv = () => {
        const transformedData: any = transformTransactionsForExport(filteredUsersList, showEarningsAsGross, timezone);

        const csv = parseCsv(transformedData.rows, {
            fields: transformedData.headers,
            transforms: [],
        });

        const blob: Blob = new Blob([csv], { type: 'text/csv' });
        const filename: string = `users_${metricId}_${moment().tz(timezone, true).format('YYYY-MM-DD')}.csv`;

        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);

        document.body.appendChild(link);
        link.click();
        link.parentNode && link.parentNode.removeChild(link);
    };

    // Convert to Excel format and start file download
    const handleDownloadReportExcel = () => {
        const transformedData: any = transformTransactionsForExport(filteredUsersList, showEarningsAsGross, timezone);

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet([]);

        XLSX.utils.sheet_add_aoa(worksheet, [transformedData.headers.map((header: any) => header.label)]);

        XLSX.utils.sheet_add_json(worksheet, transformedData.rows, {
            origin: 'A2',
            skipHeader: true,
            header: transformedData.headers.map((header: any) => header.value),
        });

        XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');

        const filename: string = `users_${metricId}_${moment().tz(timezone, true).format('YYYY-MM-DD')}.xlsx`;

        XLSX.writeFile(workbook, filename);
    };

    const metricName = metricType ? metricTypeName(metricType) : 'Metric';
    const sortField = reportSort[0]?.field || 'total';
    const sortDirection = reportSort[0]?.sort || 'desc';
    const toolbarControlSx = { height: 56, backgroundColor: '#ffffff', borderRadius: 1.5 };

    return (
        <Box>
            <Typography variant="h5" sx={{ marginBottom: 1.5 }}>
                Subscribers and Sales
            </Typography>

            <Alert
                severity="info"
                icon={<InfoIcon fontSize="large" />}
                sx={{
                    marginBottom: 2,
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'info.light',
                    alignItems: 'flex-start',
                    '& .MuiAlert-message': { fontSize: '1rem', lineHeight: 1.6 },
                }}
            >
                <div>
                    The following is a list of users who{' '}
                    {metricType === 'promoCampaign' ? 'subscribed via this campaign' : 'claimed this trial/promo'}, when they
                    {metricType === 'promoCampaign' ? ' subscribed' : ' claimed it'} and how much they had spent in total (
                    {showEarningsAsGross ? 'gross' : 'net'}) since the {metricType === 'promoCampaign' ? 'campaign' : 'trial/promo'}{' '}
                    started.
                </div>
                {metricType === 'promoCampaign' && (
                    <div>
                        If you don't see the subscription date yet, please come back later. The system is slowly fetching this information
                        from OnlyFans in the background.
                    </div>
                )}
                <div>Click on the user ID or username to open their OnlyFans profile in a new window.</div>
                <div>
                    Users flagged with a{' '}
                    <WarningIcon sx={{ fontSize: 18, verticalAlign: 'text-bottom', color: theme.palette.warning.main }} /> are identified as
                    potentially suspicious.
                </div>
            </Alert>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ marginBottom: 2 }}>
                <SextforceMetricsCountChip
                    label="All"
                    count={dateFilteredUsers.length}
                    color="primary"
                    selected={filterUsers === 'all'}
                    onClick={() => setFilterUsers('all')}
                    tooltip="Everyone found"
                />
                <SextforceMetricsCountChip
                    label="Before"
                    count={countUsersSubscribedBeforeStartDate(dateFilteredUsers, metricStartDate)}
                    color="warning"
                    selected={filterUsers === 'previouslySubscribed'}
                    onClick={() => setFilterUsers('previouslySubscribed')}
                    tooltip={`Subscribed before the ${metricName.toLowerCase()} started`}
                />
                <SextforceMetricsCountChip
                    label="Spenders"
                    count={spendersCount}
                    color="primary"
                    selected={filterUsers === 'spenders'}
                    onClick={() => setFilterUsers('spenders')}
                    tooltip="Spent something"
                />
                <SextforceMetricsCountChip
                    label="Freeloaders"
                    count={freeloadersCount}
                    color="error"
                    selected={filterUsers === 'freeloaders'}
                    onClick={() => setFilterUsers('freeloaders')}
                    tooltip="Spent nothing"
                />
                <SextforceMetricsCountChip
                    label="Chargebacks"
                    count={chargebacksCount}
                    color="error"
                    selected={filterUsers === 'chargebacks'}
                    onClick={() => setFilterUsers('chargebacks')}
                    tooltip="Have at least one chargeback"
                />
                {suspiciousCount > 0 && (
                    <SextforceMetricsCountChip
                        label="Suspicious"
                        count={suspiciousCount}
                        color="warning"
                        icon={<WarningIcon fontSize="small" />}
                        selected={filterUsers === 'suspicious'}
                        onClick={() => setFilterUsers('suspicious')}
                        tooltip="Flagged as potentially suspicious"
                    />
                )}
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ marginBottom: 1.5 }}>
                <TextField
                    fullWidth
                    placeholder="Search by ID or username..."
                    value={search}
                    inputProps={{ 'aria-label': 'Search by ID or username' }}
                    sx={{ '& .MuiOutlinedInput-root': { ...toolbarControlSx } }}
                    onChange={e => setSearch(e.currentTarget.value)}
                    onKeyDown={e => {
                        if (e.key === 'Escape') {
                            setSearch('');
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: search.length > 0 && (
                            <InputAdornment position="end">
                                <IconButton aria-label="Clear search" onClick={() => setSearch('')} size="small">
                                    <ClearIcon color="error" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <ButtonGroup
                    variant="outlined"
                    color="inherit"
                    sx={{ flexShrink: 0, '& .MuiButton-root': { ...toolbarControlSx, borderColor: 'divider' } }}
                >
                    <Tooltip title="Filter by subscription date">
                        <Button
                            onClick={openSubscribedDateDialog}
                            startIcon={<DateRangeIcon color="primary" />}
                            endIcon={isSubscribedDateFilterActive ? undefined : <ArrowDropDownIcon />}
                            aria-haspopup="dialog"
                            sx={{
                                textTransform: 'none',
                                fontSize: '1rem',
                                fontWeight: isSubscribedDateFilterActive ? 600 : 400,
                                color: isSubscribedDateFilterActive ? 'primary.main' : 'text.primary',
                                px: 2,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {isSubscribedDateFilterActive
                                ? formatSubscribedDateRange(subscribedDateRange, isPhone ? 'l' : 'L')
                                : 'All time'}
                        </Button>
                    </Tooltip>
                    {isSubscribedDateFilterActive && (
                        <Tooltip title="Clear date filter">
                            <Button onClick={clearSubscribedDateRange} aria-label="Clear date filter" sx={{ px: 1, minWidth: 0 }}>
                                <ClearIcon color="error" fontSize="small" />
                            </Button>
                        </Tooltip>
                    )}
                </ButtonGroup>
            </Stack>

            {isCompact && (
                <Accordion
                    disableGutters
                    elevation={0}
                    variant="outlined"
                    sx={{ marginBottom: 1.5, borderRadius: 1.5, '&:before': { display: 'none' }, '&.Mui-expanded': { borderRadius: 1.5 } }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 56, px: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <FilterListIcon color="primary" />
                            <Typography variant="body1" color="primary.main">
                                Sort and filters
                            </Typography>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                            <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                                <InputLabel id="users-sort-field-label">Sort by</InputLabel>
                                <Select
                                    labelId="users-sort-field-label"
                                    label="Sort by"
                                    value={sortField}
                                    onChange={e => setReportSort([{ field: String(e.target.value), sort: sortDirection }])}
                                >
                                    {USER_SORT_OPTIONS.map(option => (
                                        <MenuItem key={option.field} value={option.field}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                color="primary"
                                value={sortDirection}
                                onChange={(_e, value: 'asc' | 'desc' | null) => {
                                    if (value) {
                                        setReportSort([{ field: sortField, sort: value }]);
                                    }
                                }}
                                aria-label="Sort direction"
                            >
                                <ToggleButton value="desc" sx={{ textTransform: 'none', px: 2 }}>
                                    High to low
                                </ToggleButton>
                                <ToggleButton value="asc" sx={{ textTransform: 'none', px: 2 }}>
                                    Low to high
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            )}

            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ marginBottom: 1.5 }}>
                <Typography variant="body1" color="text.secondary" aria-live="polite">
                    {promoCampaignTotalSalesLoading ? 'Loading…' : `${searchedRows.length.toLocaleString()} results`}
                </Typography>
                <Stack direction="row" spacing={1.5}>
                    <Tooltip title="Download as CSV">
                        <span>
                            <Button
                                variant="outlined"
                                color="inherit"
                                disabled={!metricId}
                                onClick={handleDownloadReportCsv}
                                startIcon={<FontAwesomeIcon icon={faFileCsv} color={theme.palette.primary.main} />}
                                sx={{ ...toolbarControlSx, borderColor: 'divider', color: 'primary.main', px: 2 }}
                            >
                                CSV
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Download as Excel">
                        <span>
                            <Button
                                variant="outlined"
                                color="inherit"
                                disabled={!metricId}
                                onClick={handleDownloadReportExcel}
                                startIcon={<FontAwesomeIcon icon={faFileExcel} color={theme.palette.primary.main} />}
                                sx={{ ...toolbarControlSx, borderColor: 'divider', color: 'primary.main', px: 2 }}
                            >
                                XLSX
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            {isCompact ? (
                <Box sx={{ marginBottom: theme.spacing(4) }}>
                    <SextforceMetricsUsersWithRiskLevelCards
                        rows={cardRows}
                        loading={promoCampaignTotalSalesLoading}
                        searchActive={search.trim().length > 0}
                        metricStartDate={metricStartDate}
                        timezone={timezone}
                        theme={theme}
                        showEarningsAsGross={showEarningsAsGross}
                        showEarningsWithSubscriptions={showEarningsWithSubscriptions}
                        limit={limit}
                        offset={offset}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        onLimitChange={setLimit}
                        onOffsetChange={setOffset}
                    />
                </Box>
            ) : (
                <Box
                    sx={{
                        width: '100%',
                        '.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },
                        marginBottom: theme.spacing(4),
                    }}
                >
                    <DataGridPro
                        rows={processedRows}
                        columns={columns}
                        density={'compact'}
                        autoHeight={false}
                        disableColumnFilter
                        disableColumnMenu
                        filterMode="client"
                        rowCount={filteredUsersList.length}
                        getRowId={row => row._id}
                        paginationMode={'client'}
                        paginationModel={{ page: promoCampaignTotalSales ? offset / limit : 0, pageSize: limit }}
                        onPaginationModelChange={model => {
                            if (model.pageSize !== limit) {
                                setLimit(model.pageSize);
                            }

                            if (model.page !== offset / limit) {
                                setOffset(model.page * limit);
                            }
                        }}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        filterModel={filter}
                        sortingMode={'client'}
                        sortModel={reportSort}
                        onSortModelChange={(model: GridSortModel) => setReportSort(model)}
                        slots={{
                            noRowsOverlay: () => (
                                <Stack height="100%" alignItems="center" justifyContent="center">
                                    No Subscribers Found
                                </Stack>
                            ),
                            noResultsOverlay: () => (
                                <Stack height="100%" alignItems="center" justifyContent="center">
                                    No Results Matching Your Search
                                </Stack>
                            ),
                        }}
                        loading={promoCampaignTotalSalesLoading}
                        disableVirtualization={false}
                        rowBuffer={10}
                        columnBuffer={2}
                        rowThreshold={3}
                        sx={{ height: 600, backgroundColor: '#ffffff', borderRadius: 2 }}
                    />
                </Box>
            )}

            <DialogAdaptive
                open={subscribedDateDialogOpen}
                onClose={() => setSubscribedDateDialogOpen(false)}
                maxWidth={isPhone ? 'xs' : isCompact ? 'sm' : 'md'}
                fullWidth={isCompact}
            >
                <StyledCardTitleBar title="Filter by Subscription Date" />
                <DialogContent sx={{ px: { xs: 1, sm: 2 }, overflowX: 'hidden' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Only show users who {metricType === 'promoCampaign' ? 'subscribed' : 'claimed this'} between the selected dates (
                        {timezone}).
                    </Typography>
                    <Box display="flex" justifyContent="center" sx={{ width: '100%' }}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <StaticDateRangePicker
                                displayStaticWrapperAs={isPhone ? 'mobile' : 'desktop'}
                                value={draftSubscribedDateRange}
                                onChange={value => setDraftSubscribedDateRange(value)}
                                maxDate={moment().endOf('day')}
                                calendars={isCompact ? 1 : 2}
                                slotProps={{ actionBar: { actions: [] } }}
                                sx={{
                                    maxWidth: '100%',
                                    '& .MuiPickersLayout-root': { minWidth: 0 },
                                }}
                            />
                        </LocalizationProvider>
                    </Box>
                </DialogContent>
                <StyledDialogActions>
                    {isSubscribedDateFilterActive && (
                        <Button onClick={clearSubscribedDateRange} color="error" sx={{ marginRight: 'auto' }}>
                            Clear
                        </Button>
                    )}
                    <Button onClick={() => setSubscribedDateDialogOpen(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={applySubscribedDateRange}
                        variant="contained"
                        disabled={!isSubscribedDateRangeActive(draftSubscribedDateRange)}
                    >
                        Apply
                    </Button>
                </StyledDialogActions>
            </DialogAdaptive>
        </Box>
    );
};

export default SextforceMetricsUsersWithRiskLevelList;
